import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps the cart marker on the shipping scale in step with the cart. Where the cart sits and what
 * is missing to the next tier is worked out in `snippets/shipping-terms.liquid`, from the same
 * state as the cart message, so this element only fetches its own section again when the cart
 * changes and swaps its content.
 *
 * @extends Component
 */
class ShippingScaleProgress extends Component {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /** @param {Event & { promise?: Promise<unknown> }} event */
  #onCartUpdate = (event) => {
    // Refresh whether the cart request succeeded or not; the section shows what the cart holds.
    Promise.resolve(event.promise)
      .catch(() => undefined)
      .then(() => this.#refresh())
      .catch(() => undefined);
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#refresh().catch(() => undefined);
  };

  async #refresh() {
    const { sectionId } = this.dataset;
    if (!sectionId) return;

    try {
      const url = new URL(location.href);
      url.searchParams.set('section_id', sectionId);
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) return;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('shipping-scale-progress');
      if (fresh) this.replaceChildren(...fresh.childNodes);
    } catch {
      // Keep the last rendered scale if the section cannot be fetched.
    }
  }
}

if (!customElements.get('shipping-scale-progress')) {
  customElements.define('shipping-scale-progress', ShippingScaleProgress);
}
